//-----------------------------------------------------------------------------
// This Class handles the Application State with regard to the Menu's and
// any other generic Application States that are not to do with business
// logic
//-----------------------------------------------------------------------------

import { BehaviorSubject } from 'rxjs';
import * as singleSpa from 'single-spa';

import * as NavBarState from '../../types/navBarState';
import { BamboraConfig } from '../../types/bambora';
import { DirectDebitData } from '../../types/directDebit';
import { ConcessionData } from '../../types/concession';

// We must use a require() or WebPack will not compile this file as
// this package will be supplied at runtime
// eslint-disable-next-line @typescript-eslint/no-var-requires
const authPkg = require('@alinta-digital/myaccount-frontend-api-authentication');
const userDataKey = 'userData';

/***
  @singleton decorator
  add static method getInstance(...args) to decorated class which returns single instance 
  (or constructs single instance if it doesn't exist)
  
***/

function singleton(Target: any) {
  //static instance getter method
  Target.getInstance = function (...args: any[]) {
    // save a reference to the original constructor
    const original = Target;

    // a utility function to generate instance of a class
    function construct(constructor) {
      const c: any = function () {
        return constructor.apply(this, args);
      };
      c.prototype = constructor.prototype;
      return new c();
    }

    //new constructor
    const f: any = function () {
      return construct(original);
    };

    if (!original.instance) {
      // copy prototype so intanceof operator still works
      f.prototype = original.prototype;
      original.instance = new f();
    }

    return original.instance;
  };
}

// Just mock these up for the moment so that we have something
// to call
@singleton
export class AppStateHelper {
  // Hold onto the subscription for the Authenticator
  private authSubscription = null;

  // Store the URL's for the backend that can be configured
  private urlMap = {};

  // User Data
  private userData = {};

  // Local User Data
  private localUserData = {};

  // Flag that the currently dislayed page is a property page
  // This changes how the menu is displayed
  private isPropertyPage: boolean = false;

  // Allow Apps to force display the footer even
  // if not logged in
  private forceDisplayFooter: boolean = false;

  //allows apps to know to render navbar when in logout in progress
  private isLogoutInprogress: boolean = false;

  // Manage the Subscription for changes to the Footer
  // Display Status
  public footer$ = new BehaviorSubject({
    display: true
  });

  // Manage the Subscription for changes to the NavBar
  public navBar$ = new BehaviorSubject({
    authenticated: false,
    websiteLink: {},
    homePageLink: {},
    leftNavigationItems: [],
    rightNavigationItems: [],
    isLogoutInprogress: false
  });

  private sessionTimeoutInMilliseconds: number = 3600000;

  private bamboraConfig: BamboraConfig = {
    merchantId: '',
    url: ''
  };

  private buildEnv: string = 'development';

  //private rewardExternalUri: string = '';

  // Returns the Singleton instance for the App State
  public static getInstance(): AppStateHelper {
    return undefined;
  }

  //-------------------------------------------------------
  // Names of the Links
  public static readonly OVERVIEW = 'Overview';
  public static readonly USAGE = 'Usage';
  public static readonly BILLS_PAYMENTS = 'Bills & Payments';
  //public static readonly REWARDS = 'Rewards';
  public static readonly SETTINGS = 'Settings';
  public static readonly ACCOUNT_SELECTION = 'Accounts';
  public static readonly LOGIN = 'Login';
  public static readonly ACTIVATE = 'Activate';
  public static readonly LOGOUT = 'Logout';
  public static readonly ERROR = 'Error';
  public static readonly CHANGE_PLAN = 'Change Plan';
  public static readonly PAYMENT_EXTENSION = 'Payment Extension';
  public static readonly DIRECT_DEBIT = 'Direct Debit';
  public static readonly VIEW_PLAN = 'View Plan';
  public static readonly ADD_PROPERTY = 'Add Property';
  public static readonly CONCESSION = 'Concession';
  public static readonly SUBMIT_METER_READ = 'Submit Meter Read';
  public static readonly ADD_FUEL = 'Add Fuel';
  public static readonly BALANCE_TRANSFER = 'Balance Transfer';

  // Links that can be navigated to
  public static OVERVIEW_LINK = '/overview';
  public static USAGE_LINK = '/usage';
  public static BILLS_PAYMENTS_LINK = '/payments';
  //public static REWARDS_LINK = '/rewards';
  public static SETTINGS_LINK = '/settings';
  public static ACCOUNT_SELECTION_LINK = '/accounts';
  public static LOGIN_LINK = '/login';
  public static ACTIVATE_LINK = '/activate';
  public static LOGOUT_LINK = '/logout';
  public static ERROR_LINK = '/error';
  public static CHANGE_PLAN_LINK = '/changeplan';
  public static PAYMENT_EXTENSION_LINK = '/paymentextension';
  public static DIRECT_DEBIT_LINK = '/directdebit';
  public static VIEW_PLAN_LINK = '/yourplan';
  public static ADD_PROPERTY_LINK = '/addproperty';
  public static CONCESSION_LINK = '/concession';
  public static ADD_FUEL_LINK = '/addfuel';
  public static BALANCE_TRANSFER_LINK = '/balancetransfer';

  /** ID for writing to deep link info to local storage and reading from url */
  public static readonly DEEP_LINK_ID = 'deepLink';
  public static SUBMIT_METER_READ_LINK = '/submit-meter-read';

  //-------------------------------------------------------
  // Setup all of the links
  private overviewLink = {
    name: AppStateHelper.OVERVIEW,
    href: AppStateHelper.OVERVIEW_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-dashboard'
  };

  private usageLink = {
    name: AppStateHelper.USAGE,
    href: AppStateHelper.USAGE_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-usage'
  };

  private billsPaymentsLink = {
    name: AppStateHelper.BILLS_PAYMENTS,
    href: AppStateHelper.BILLS_PAYMENTS_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-payments'
  };


  // private rewardsLink = {
  //   name: AppStateHelper.REWARDS,
  //   href: AppStateHelper.REWARDS_LINK,
  //   isPropertyPage: false,
  //   appName: '@alinta-digital/myaccount-frontend-rewards'
  // };


  private settingsLink = {
    name: AppStateHelper.SETTINGS,
    href: AppStateHelper.SETTINGS_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-settings'
  };

  private loginLink = {
    name: AppStateHelper.LOGIN,
    href: AppStateHelper.LOGIN_LINK,
    isPropertyPage: true,
    appName: '@alinta-digital/myaccount-frontend-login'
  };

  private activateLink = {
    name: AppStateHelper.ACTIVATE,
    href: AppStateHelper.ACTIVATE_LINK,
    isPropertyPage: true,
    appName: '@alinta-digital/myaccount-frontend-activation'
  };

  private logoutLink = {
    name: AppStateHelper.LOGOUT,
    href: AppStateHelper.LOGOUT_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-logout'
  };

  private errorLink = {
    name: AppStateHelper.ERROR,
    href: AppStateHelper.ERROR_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-error'
  };

  private accountSelectionLink = {
    name: AppStateHelper.ACCOUNT_SELECTION,
    href: AppStateHelper.ACCOUNT_SELECTION_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-account-selection'
  };

  private readonly changePlanLink = {
    name: AppStateHelper.CHANGE_PLAN,
    href: AppStateHelper.CHANGE_PLAN_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-add-property'
  };

  private readonly balanceTransferLink = {
    name: AppStateHelper.BALANCE_TRANSFER,
    href: AppStateHelper.BALANCE_TRANSFER_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-balance-transfer'
  };

  private readonly paymentExtensionLink = {
    name: AppStateHelper.PAYMENT_EXTENSION,
    href: AppStateHelper.PAYMENT_EXTENSION_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-payment-extension'
  };
  private readonly directDebitLink = {
    name: AppStateHelper.DIRECT_DEBIT,
    href: AppStateHelper.DIRECT_DEBIT_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-direct-debit'
  };

  private readonly viewPlanLink = {
    name: AppStateHelper.VIEW_PLAN,
    href: AppStateHelper.VIEW_PLAN_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-view-plan'
  };

  private readonly addPropertyLink = {
    name: AppStateHelper.ADD_PROPERTY,
    href: AppStateHelper.ADD_PROPERTY_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-add-property'
  };

  private readonly concessionLink = {
    name: AppStateHelper.CONCESSION,
    href: AppStateHelper.CONCESSION_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-concession'
  };

  private readonly addFuelLink = {
    name: AppStateHelper.ADD_FUEL,
    href: AppStateHelper.ADD_FUEL_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-add-property'
  };

  private readonly submitMeterReadLink = {
    name: AppStateHelper.SUBMIT_METER_READ,
    href: AppStateHelper.SUBMIT_METER_READ_LINK,
    isPropertyPage: false,
    appName: '@alinta-digital/myaccount-frontend-submit-meter-read'
  };

  private readonly websiteLink = {
    name: 'WebSite',
    href: 'https://www.alintaenergy.com.au',
    isPropertyPage: false,
    appName: ''
  };

  // Setup the Link Arrays
  private readonly logoutNavigation = [this.logoutLink];
  private readonly leftNavigationItems = [this.overviewLink, this.billsPaymentsLink, this.usageLink]; //, this.rewardsLink];
  private readonly rightNavigationItems = [this.settingsLink, this.logoutLink];
  private readonly loginNavigation = [this.loginLink];
  private readonly activateNavigation = [this.activateLink];
  private readonly allApps = [
    this.activateLink,
    this.loginLink,
    this.logoutLink,
    this.accountSelectionLink,
    this.errorLink,
    this.overviewLink,

    // this.rewardsLink,

    this.settingsLink,
    this.billsPaymentsLink,
    this.changePlanLink,
    this.paymentExtensionLink,
    this.directDebitLink,
    this.usageLink,
    this.viewPlanLink,
    this.addPropertyLink,
    this.concessionLink,
    this.submitMeterReadLink,
    this.addFuelLink,
    this.balanceTransferLink
  ];

  private directDebitData: DirectDebitData = {
    savedPaymentMethod: {
      hasSavedNewPaymentMethod: false,
      isSaved: false
    },
    redirectUrl: this.overviewLink.name
  };

  private concessionData: ConcessionData = {
    redirectUrl: this.overviewLink.name,
    isSaved: false,
    hasError: false
  };

  //-------------------------------------------------------
  // Forces a navigation to the given reference as
  // selected by the 'name'
  //
  // Overview, Usage, Bills & Payments,
  // Rewards, Settings, Login, Logout,
  // Error, Accounts
  public navigateTo(name: string, props: any = null): void {
    this.isLogoutInprogress = false;
    if (name.toLocaleLowerCase() === AppStateHelper.LOGOUT.toLocaleLowerCase()) {
      this.isLogoutInprogress = true;
    }

    const state = this.getNavBarState();
    const lcName = name.toLocaleLowerCase();

    const link =
      state.leftNavigationItems.find((e) => e.name.toLocaleLowerCase() === lcName) ??
      state.rightNavigationItems.find((e) => e.name.toLocaleLowerCase() === lcName) ??
      (state.loginPageLink.name.toLocaleLowerCase() === lcName ? state.loginPageLink : null) ??
      (lcName === AppStateHelper.ACCOUNT_SELECTION.toLocaleLowerCase() ? this.accountSelectionLink : null) ??
      (lcName === AppStateHelper.ERROR.toLocaleLowerCase() ? this.errorLink : null) ??
      (lcName === AppStateHelper.LOGOUT.toLocaleLowerCase() ? this.logoutLink : null) ??
      (lcName === AppStateHelper.CHANGE_PLAN.toLocaleLowerCase() ? this.changePlanLink : null) ??
      (lcName === AppStateHelper.DIRECT_DEBIT.toLocaleLowerCase() ? this.directDebitLink : null) ??
      (lcName === AppStateHelper.VIEW_PLAN.toLocaleLowerCase() ? this.viewPlanLink : null) ??
      (lcName === AppStateHelper.BILLS_PAYMENTS_LINK.toLocaleLowerCase() ? this.billsPaymentsLink : null) ??
      (lcName === AppStateHelper.CONCESSION.toLocaleLowerCase() ? this.concessionLink : null) ??
      (lcName === AppStateHelper.ADD_PROPERTY.toLocaleLowerCase() ? this.addPropertyLink : null) ??
      (lcName === AppStateHelper.ADD_FUEL.toLocaleLowerCase() ? this.addFuelLink : null) ??
      (lcName === AppStateHelper.BALANCE_TRANSFER.toLocaleLowerCase() ? this.balanceTransferLink : null) ??
      (lcName === AppStateHelper.PAYMENT_EXTENSION.toLocaleLowerCase() ? this.paymentExtensionLink : null) ??
      (lcName === AppStateHelper.SUBMIT_METER_READ.toLocaleLowerCase() ? this.submitMeterReadLink : null);

    if (link !== null) {
      let url = link.href;

      if (props !== null) {
        url += '?' + new URLSearchParams(props).toString();
      }
      singleSpa.navigateToUrl(url);

      // Notify of changes
      this.navBar$.next({
        ...this.getNavBarState()
      });
    }
  }

  // Navigate to the Login Page if we are not logged in
  public navigateToLogin(): boolean {
    this.isLogoutInprogress = false;

    if (!authPkg.getAuthenticator().isAuthenticated()) {
      this.setDeepLink();
      this.navigateTo(this.getNavBarState().loginPageLink.name);

      // Notify of changes
      this.navBar$.next({
        ...this.getNavBarState()
      });
      return true;
    }
    return false;
  }

  /** Set route name to local storage for the enabled links */
  private setDeepLink(): void {
    // note: add links to the array when needed
    const links = [
      this.billsPaymentsLink,
      this.usageLink,
      this.directDebitLink,
      this.concessionLink,
      this.addFuelLink,
      this.addPropertyLink,
      this.viewPlanLink,
      this.changePlanLink,
      this.settingsLink,
      this.paymentExtensionLink,
      this.submitMeterReadLink
    ];
    const routeName = links.find((link) => link.href === location.pathname)?.name;
    if (!routeName) {
      return;
    }
    localStorage.setItem(AppStateHelper.DEEP_LINK_ID, routeName);
  }

  /**
   * Navigate to deep link if exists.
   * Returns true if navigated.
   */
  public navigateToDeepLink(): boolean {
    const deepLinkRouteName = localStorage.getItem(AppStateHelper.DEEP_LINK_ID);
    if (!deepLinkRouteName) {
      return false;
    }
    localStorage.removeItem(AppStateHelper.DEEP_LINK_ID);
    this.navigateTo(deepLinkRouteName);
    return true;
  }

  // Navigate to the Logout Page if we are not logged in
  public navigateToLogout(): boolean {
    this.isLogoutInprogress = true;
    this.navigateTo(this.logoutLink.name);

    // Notify of changes
    this.navBar$.next({
      ...this.getNavBarState()
    });
    return true;
  }

  // Sets the URL for the given name
  public setUrl(name: string, url: string): void {
    this.urlMap[name] = url;

    if (name === 'REACT_APP_BASE_URL') {
      AppStateHelper.OVERVIEW_LINK = `${url}/overview`;
      AppStateHelper.USAGE_LINK = `${url}/usage`;
      AppStateHelper.BILLS_PAYMENTS_LINK = `${url}/payments`;
  // AppStateHelper.REWARDS_LINK = `${url}/rewards`;

      AppStateHelper.SETTINGS_LINK = `${url}/settings`;
      AppStateHelper.ACCOUNT_SELECTION_LINK = `${url}/accounts`;
      AppStateHelper.LOGIN_LINK = `${url}/login`;
      AppStateHelper.ACTIVATE_LINK = `${url}/activate`;
      AppStateHelper.LOGOUT_LINK = `${url}/logout`;
      AppStateHelper.ERROR_LINK = `${url}/error`;
      AppStateHelper.CHANGE_PLAN_LINK = `${url}/changeplan`;
      AppStateHelper.PAYMENT_EXTENSION_LINK = `${url}/paymentextension`;
      AppStateHelper.DIRECT_DEBIT_LINK = `${url}/directdebit`;
      AppStateHelper.VIEW_PLAN_LINK = `${url}/yourplan`;
      AppStateHelper.ADD_PROPERTY_LINK = `${url}/addproperty`;
      AppStateHelper.CONCESSION_LINK = `${url}/concession`;
      AppStateHelper.ADD_FUEL_LINK = `${url}/addfuel`;
      AppStateHelper.SUBMIT_METER_READ_LINK = `${url}/submit-meter-read`;
      AppStateHelper.BALANCE_TRANSFER_LINK = `${url}/balancetransfer`;

      this.accountSelectionLink.href = AppStateHelper.ACCOUNT_SELECTION_LINK;
      this.logoutLink.href = AppStateHelper.LOGOUT_LINK;
      this.loginLink.href = AppStateHelper.LOGIN_LINK;
      this.activateLink.href = AppStateHelper.ACTIVATE_LINK;
      this.overviewLink.href = AppStateHelper.OVERVIEW_LINK;
      this.errorLink.href = AppStateHelper.ERROR_LINK;
      this.settingsLink.href = AppStateHelper.SETTINGS_LINK;
      this.usageLink.href = AppStateHelper.USAGE_LINK;

      // this.rewardsLink.href = AppStateHelper.REWARDS_LINK;

      this.billsPaymentsLink.href = AppStateHelper.BILLS_PAYMENTS_LINK;
      this.changePlanLink.href = AppStateHelper.CHANGE_PLAN_LINK;
      this.paymentExtensionLink.href = AppStateHelper.PAYMENT_EXTENSION_LINK;
      this.directDebitLink.href = AppStateHelper.DIRECT_DEBIT_LINK;
      this.viewPlanLink.href = AppStateHelper.VIEW_PLAN_LINK;
      this.addPropertyLink.href = AppStateHelper.ADD_PROPERTY_LINK;
      this.concessionLink.href = AppStateHelper.CONCESSION_LINK;
      this.addFuelLink.href = AppStateHelper.ADD_FUEL_LINK;
      this.submitMeterReadLink.href = AppStateHelper.SUBMIT_METER_READ_LINK;
      this.balanceTransferLink.href = AppStateHelper.BALANCE_TRANSFER_LINK;

      // Flag the changes
      this.navBar$.next({
        ...this.getNavBarState()
      });
    }
  }

  // Gets the URL for the given name
  public getUrl(name: string): string | undefined {
    return this.urlMap[name];
  }

  //gets base URL
  public getBaseUrl(): string | undefined {
    return this.urlMap['REACT_APP_BASE_URL'];
  }

  // Gets the User Data
  public getUserData(): any {
    this.userData = JSON.parse(localStorage.getItem(userDataKey));
    return this.userData;
  }

  // Sets the User Data
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setUserData(userData: any): void {
    this.userData = userData;
    localStorage.setItem(userDataKey, JSON.stringify(userData));
  }

  // Get the user data field by name
  public getUserDataField(name: string): any {
    if (name == undefined || name == null || name == '' || this.userData == undefined || this.userData == null) {
      return null;
    }

    if (name == 'customerNumber') {
      const csNum = authPkg.getAuthenticator().getCustomerNumber();
      if (csNum) return csNum;
      else return this.userData['ghostingcid'];
    } else if (name == 'username') {
      return authPkg.getAuthenticator().getUserName();
    }

    return this.userData[name] ?? this.localUserData[name] ?? null;
  }

  // Sets an individual field in the User Data
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setUserDataField(name: string, data: any, canStore: boolean = true): void {
    if (canStore) {
      this.userData[name] = data;
      localStorage.setItem(userDataKey, JSON.stringify(this.userData));
    } else {
      this.localUserData[name] = data;
    }
  }

  // Allow apps to request the menu state to change
  public setPropertyPage(isPropertyPage: boolean): void {
    this.isPropertyPage = isPropertyPage;

    this.navBar$.next({
      ...this.getNavBarState()
    });
  }

  // Force the display of the footer and fire the subscription
  public setDisplayFooter(display: boolean): void {
    const temp = this.forceDisplayFooter;

    this.forceDisplayFooter = display;

    if (temp != display) {
      this.footer$.next({
        display: this.displayFooter()
      });
    }
  }

  // Do we display the Footer or not?
  public displayFooter(): boolean {
    return authPkg.getAuthenticator().isAuthenticated() || this.forceDisplayFooter;
  }

  // Gets the state of the App's Navigation Bar
  public getNavBarState(): NavBarState.NavBarState {
    const auth = authPkg.getAuthenticator().isAuthenticated();
    const displayMenu = auth && !this.isPropertyPage;
    const isPropertyPage = auth && this.isPropertyPage;
    return {
      authenticated: auth,
      homePageLink: auth ? this.leftNavigationItems[0] : this.logoutNavigation[0],
      loginPageLink: this.loginNavigation[0],
      activatePageLink: this.activateNavigation[0],
      leftNavigationItems: this.isLogoutInprogress || !displayMenu ? [] : this.leftNavigationItems,
      rightNavigationItems: !auth
        ? []
        : isPropertyPage || this.isLogoutInprogress
        ? this.logoutNavigation
        : this.rightNavigationItems,
      websiteLink: this.websiteLink,
      accountSelectionLink: this.accountSelectionLink,
      errorLink: this.errorLink,
      directDebitLink: this.directDebitLink,
      isLogoutInprogress: this.isLogoutInprogress,
      addPropertyLink: this.addPropertyLink,
      addFuelLink: this.addFuelLink,
      concessionLink: this.concessionLink,
      submitMeterReadLink: this.submitMeterReadLink
    };
  }

  // Logs the user out of the application and handles
  // cleaning up any state
  public async logout(navToLogin: boolean = true): Promise<void> {
    this.isLogoutInprogress = true;

    const userData = this.getUserData();
    const finalUserData = {};

    const isGhosting: boolean = this.getUserDataField('ghostingcid');
    localStorage.removeItem(userDataKey);

    // Save this off so that we have a copy of it for later
    if (userData && userData['loginNavigateTo'] !== null) {
      finalUserData['loginNavigateTo'] = userData['loginNavigateTo'];
      finalUserData['logoutReason'] = userData['logoutReason'];
      this.setUserData(finalUserData);
    }
    // await authPkg.getAuthenticator().logout();
    //for agents logout
    this.cleanup();
    await authPkg.getAuthenticator().logout(isGhosting); //sends agentsLogout=true

    this.navBar$.next({
      ...this.getNavBarState()
    });

    const mountedAppNames = singleSpa.getAppNames();
    mountedAppNames
      .filter(
        (app) =>
          app !== '@alinta-digital/myaccount-frontend-navbar' &&
          app !== '@alinta-digital/myaccount-frontend-api-appstate' &&
          app !== '@alinta-digital/myaccount-frontend-logout'
      )
      .forEach((app) => {
        singleSpa.unloadApplication(app);
      });
    if (navToLogin) {
      this.navigateToLogin();
    }
  }

  //Log the user out incase of noactiveaccounts, sessiontimeout, or error while fetching active accounts
  public forceLogout(reason: string): void {
    //let the logout page know the reason for logout
    this.setLogoutReason(reason);

    this.navigateToLogout();
  }

  //Get the reason for logout
  public getLogoutReason(): string {
    return this.getUserDataField('logoutReason');
  }

  //Set the reason for logout
  public setLogoutReason(reason: string): void {
    this.setUserDataField('logoutReason', reason);
  }

  //Returns time in milliseconds
  public getSessionTimeout(): number {
    return authPkg.getAuthenticator().getTimeoutInMilliSeconds();
  }

  //Set session timout in milliseconds
  public setSessionTimeout(timeout: string): void {
    this.sessionTimeoutInMilliseconds = Number(timeout);
  }

  /*On session timoeut log the user out of the application
   * This method being called for Session IDLE scenario
   */
  public handleOnSessionTimeout(): void {
    this.forceLogout('sessiontimeout');
  }

  /** Navigates user to session expiry page when the user session (okta token) expires
   * This differs from handleSessionTimeout as it doesn't run in timeout
   */
  public handleOktaSessionExpiry(): void {
    setTimeout(() => {
      this.forceLogout('sessiontimeout');
    }, this.getSessionTimeout());
  }

  //Returns Rewards External URI
  public getRewardExternalUri(): string {
    const config = authPkg.getAuthenticator().getOktaConfig();
    return config.rewardsRedirectUri;
  }

  //Returns bambora config
  public getBamboraConfig(): BamboraConfig {
    return this.bamboraConfig;
  }

  //Set bambora config
  public setBamboraConfig(config: BamboraConfig): void {
    this.bamboraConfig = config;
  }

  //Returns Build environment setup in azure variables
  public getBuildEnv(): string {
    return this.buildEnv;
  }

  //Set build environment
  public setBuildEnv(buildEnv: string): void {
    this.buildEnv = buildEnv;
  }

  //Get details related to direct debit
  public getDirectDebitData(): DirectDebitData {
    return this.directDebitData;
  }

  //Get details related to concession
  public getConcessionData(): ConcessionData {
    return this.concessionData;
  }

  //Set direct debit values
  public setDirectDebitData(data: DirectDebitData): void {
    this.directDebitData = data;
  }

  //Set concession values
  public setConcessionData(data: ConcessionData): void {
    this.concessionData = data;
  }

  // We need to make this private so that others cannot use it
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {
    // TO DO: Have to work out how to unsubscribe
    //
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.authSubscription = authPkg.getAuthenticator().auth$.subscribe(({ username }) => {
      this.footer$.next({
        display: username !== ''
      });
      this.navBar$.next({
        ...this.getNavBarState()
      });
    });

    // Make sure that this always has a value
    if (this.getUserData() == null) this.setUserData({});
  }

  // Cleanup the class
  public cleanup(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
}

// Use this function to get the Instance of the class
export function getAppState(): AppStateHelper {
  return AppStateHelper.getInstance();
}
